import React, { useEffect, useState } from 'react';
import Spline from '@splinetool/react-spline';
import { ChevronRight, Volume2, Battery, Zap, Bluetooth, Award, Clock } from 'lucide-react';
import { Button } from '../components/ui/button';
import './Home.css';

const Home = () => {
  const [scrollY, setScrollY] = useState(0);
  const [isVisible, setIsVisible] = useState({});

  useEffect(() => {
    const handleScroll = () => setScrollY(window.scrollY);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          setIsVisible((prev) => ({
            ...prev,
            [entry.target.id]: entry.isIntersecting,
          }));
        });
      },
      { threshold: 0.1 }
    );

    document.querySelectorAll('.animate-on-scroll').forEach((el) => {
      observer.observe(el);
    });

    return () => observer.disconnect();
  }, []);

  const features = [
    {
      icon: <Volume2 className="w-8 h-8" />,
      title: 'Active Noise Cancellation',
      description: 'Advanced ANC technology blocks external noise for immersive listening'
    },
    {
      icon: <Battery className="w-8 h-8" />,
      title: '48 Hours Battery Life',
      description: 'Extended playtime for non-stop music enjoyment'
    },
    {
      icon: <Zap className="w-8 h-8" />,
      title: 'Ultra-Low Latency',
      description: 'Perfect sync for gaming and video with minimal delay'
    },
    {
      icon: <Award className="w-8 h-8" />,
      title: 'Hi-Res Certified Audio',
      description: 'Studio-quality sound reproduction for audiophiles'
    },
    {
      icon: <Clock className="w-8 h-8" />,
      title: 'Fast Charging',
      description: 'Just 10 minutes charge gives you 6 hours of playtime'
    },
    {
      icon: <Bluetooth className="w-8 h-8" />,
      title: 'Bluetooth 5.4',
      description: 'Latest wireless technology for stable connection'
    }
  ];

  const testimonials = [
    {
      name: 'Alex Rivera',
      role: 'Music Producer',
      image: 'https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?w=150&h=150&fit=crop',
      text: 'The Aureon X9 delivers studio-grade sound quality. The clarity and depth are phenomenal for both production and casual listening.'
    },
    {
      name: 'Sarah Chen',
      role: 'Content Creator',
      image: 'https://images.unsplash.com/photo-1494790108377-be9c29b29330?w=150&h=150&fit=crop',
      text: 'Best investment for my content creation workflow. The noise cancellation is perfect for recording in busy environments.'
    },
    {
      name: 'Marcus Johnson',
      role: 'Gamer',
      image: 'https://images.unsplash.com/photo-1500648767791-00dcc994a43e?w=150&h=150&fit=crop',
      text: 'Zero latency during competitive gaming. The sound stage is incredible - I can pinpoint every footstep and gunshot.'
    }
  ];

  const handleCTA = () => {
    window.open('https://wa.me/6281234567890?text=Hi,%20I%20want%20to%20know%20more%20about%20Aureon%20X9', '_blank');
  };

  return (
    <div className="home-container">
      {/* Header */}
      <header className="dark-header">
        <div className="header-content">
          <div className="logo-container">
            <h1 className="logo-text">AUREON</h1>
          </div>
          <nav className="dark-nav">
            <a href="#features" className="dark-nav-link">Features</a>
            <a href="#testimonials" className="dark-nav-link">Reviews</a>
            <Button className="btn-primary" onClick={handleCTA}>
              Get Yours
              <ChevronRight className="w-5 h-5" />
            </Button>
          </nav>
        </div>
      </header>

      {/* Hero Section */}
      <section className="hero-section" id="hero">
        <div className="hero-grid">
          <div className="hero-content animate-on-scroll" id="hero-content" style={{ opacity: isVisible['hero-content'] ? 1 : 0 }}>
            <div className="hero-badge">NEW ARRIVAL</div>
            <h1 className="hero-title">
              AUREON X9
            </h1>
            <p className="hero-tagline">Feel the Future of Sound</p>
            <p className="hero-description">
              Experience unparalleled audio excellence with cutting-edge technology. 
              The Aureon X9 redefines premium wireless audio.
            </p>
            <div className="hero-cta">
              <Button className="btn-primary btn-large" onClick={handleCTA}>
                Order via WhatsApp
                <ChevronRight className="w-6 h-6" />
              </Button>
              <Button className="btn-secondary btn-large" onClick={() => document.getElementById('features').scrollIntoView({ behavior: 'smooth' })}>
                Learn More
              </Button>
            </div>
          </div>
          <div className="hero-visual">
            <div className="spline-container">
              <Spline scene="https://prod.spline.design/NbVmy6DPLhY-5Lvg/scene.splinecode" />
            </div>
            <img 
              src="https://images.unsplash.com/photo-1657223143970-08cc5afe9943?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2Nzd8MHwxfHNlYXJjaHwzfHx3aXJlbGVzcyUyMGhlYWRwaG9uZXN8ZW58MHx8fGJsYWNrfDE3NjMxODUzOTJ8MA&ixlib=rb-4.1.0&q=85" 
              alt="Aureon X9 Headphone"
              className="hero-product-image"
            />
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="features-section" id="features">
        <div className="section-content">
          <div className="section-header animate-on-scroll" id="features-header" style={{ opacity: isVisible['features-header'] ? 1 : 0 }}>
            <h2 className="section-title">Engineered for Excellence</h2>
            <p className="section-subtitle">Advanced features that set new standards in wireless audio</p>
          </div>
          <div className="features-grid">
            {features.map((feature, index) => (
              <div 
                key={index} 
                className={`feature-card animate-on-scroll`}
                id={`feature-${index}`}
                style={{ 
                  opacity: isVisible[`feature-${index}`] ? 1 : 0,
                  transform: isVisible[`feature-${index}`] ? 'translateY(0)' : 'translateY(20px)',
                  transitionDelay: `${index * 100}ms`
                }}
              >
                <div className="feature-icon">{feature.icon}</div>
                <h3 className="feature-title">{feature.title}</h3>
                <p className="feature-description">{feature.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Product Showcase */}
      <section className="showcase-section">
        <div className="showcase-content">
          <div className="showcase-images">
            <img 
              src="https://images.unsplash.com/photo-1602748152742-ae1273aaab4f?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDk1ODF8MHwxfHNlYXJjaHwzfHxwcmVtaXVtJTIwaGVhZHBob25lc3xlbnwwfHx8YmxhY2t8MTc2MzE4NTM4N3ww&ixlib=rb-4.1.0&q=85"
              alt="Aureon X9 Detail"
              className="showcase-image animate-on-scroll"
              id="showcase-1"
              style={{ opacity: isVisible['showcase-1'] ? 1 : 0 }}
            />
          </div>
          <div className="showcase-text animate-on-scroll" id="showcase-text" style={{ opacity: isVisible['showcase-text'] ? 1 : 0 }}>
            <h2 className="showcase-title">Premium Design Meets Superior Comfort</h2>
            <p className="showcase-description">
              Crafted with precision-engineered materials and memory foam cushions, 
              the Aureon X9 delivers unmatched comfort for extended listening sessions. 
              Every detail is designed to perfection.
            </p>
            <ul className="showcase-list">
              <li>Aerospace-grade aluminum frame</li>
              <li>Protein leather ear cushions</li>
              <li>Ergonomic adjustable headband</li>
              <li>Foldable design with premium case</li>
            </ul>
          </div>
        </div>
      </section>

      {/* Testimonials */}
      <section className="testimonials-section" id="testimonials">
        <div className="section-content">
          <div className="section-header animate-on-scroll" id="testimonials-header" style={{ opacity: isVisible['testimonials-header'] ? 1 : 0 }}>
            <h2 className="section-title">Loved by Professionals</h2>
            <p className="section-subtitle">See what our customers have to say</p>
          </div>
          <div className="testimonials-grid">
            {testimonials.map((testimonial, index) => (
              <div 
                key={index} 
                className="testimonial-card animate-on-scroll"
                id={`testimonial-${index}`}
                style={{ 
                  opacity: isVisible[`testimonial-${index}`] ? 1 : 0,
                  transform: isVisible[`testimonial-${index}`] ? 'translateY(0)' : 'translateY(20px)',
                  transitionDelay: `${index * 100}ms`
                }}
              >
                <p className="testimonial-text">"{testimonial.text}"</p>
                <div className="testimonial-author">
                  <img src={testimonial.image} alt={testimonial.name} className="testimonial-avatar" />
                  <div>
                    <p className="testimonial-name">{testimonial.name}</p>
                    <p className="testimonial-role">{testimonial.role}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="cta-section">
        <div className="cta-content animate-on-scroll" id="cta-content" style={{ opacity: isVisible['cta-content'] ? 1 : 0 }}>
          <h2 className="cta-title">Ready to Experience the Future?</h2>
          <p className="cta-description">
            Join thousands of satisfied customers and elevate your audio experience today.
          </p>
          <Button className="btn-primary btn-large" onClick={handleCTA}>
            Order Now via WhatsApp
            <ChevronRight className="w-6 h-6" />
          </Button>
        </div>
      </section>

      {/* Footer */}
      <footer className="footer">
        <div className="footer-content">
          <div className="footer-brand">
            <h3 className="footer-logo">AUREON</h3>
            <p className="footer-tagline">Feel the Future of Sound</p>
          </div>
          <div className="footer-social">
            <h4 className="footer-title">Follow Us</h4>
            <div className="social-links">
              <a href="https://instagram.com/aureon.official" target="_blank" rel="noopener noreferrer" className="social-link">
                Instagram: @aureon.official
              </a>
              <a href="https://tiktok.com/@aureon.audio" target="_blank" rel="noopener noreferrer" className="social-link">
                TikTok: @aureon.audio
              </a>
              <a href="https://youtube.com/@AureonTech" target="_blank" rel="noopener noreferrer" className="social-link">
                YouTube: AureonTech
              </a>
            </div>
          </div>
        </div>
        <div className="footer-bottom">
          <p className="footer-copyright">© HariCompanyOfTechnology - All rights reserved</p>
        </div>
      </footer>
    </div>
  );
};

export default Home;